import type { ReactNode } from "react"

import { cn } from "@/lib/cn"

/**
 * Botón. **Única fuente de verdad de su forma.**
 *
 * ⚠ **`type` es obligatorio en la práctica y por eso su defecto es `button`.** El
 * defecto del navegador es `submit`, así que un botón dentro de un formulario que
 * sólo quería abrir un panel lo envía — un fallo que no da error, sólo hace algo
 * distinto de lo que dice el rótulo. Aquí hay que pedir `submit` a propósito.
 *
 * ⚠ **La variante `peligro` no es un color: es una promesa.** Se reserva para lo
 * que no se puede deshacer con otro clic —cerrar un expediente, anular una
 * transacción—. Usarla para «Cancelar» la gasta, y el día que aparezca la de verdad
 * nadie se detendrá.
 *
 * Las clases base viven en `estilos/globales.css` (`.boton-primario`,
 * `.boton-secundario`) para que también se puedan usar sin componente; este
 * envoltorio es lo canónico y añade tamaños y estados.
 */

export type VarianteBoton = "primario" | "secundario" | "fantasma" | "peligro"
export type TamanoBoton = "sm" | "md"

const VARIANTE: Record<VarianteBoton, string> = {
  primario: "boton-primario",
  secundario: "boton-secundario",
  // Sin borde ni relleno hasta que se apunta: para acciones repetidas en una fila,
  // donde un botón con marco por celda convierte la tabla en una cuadrícula.
  fantasma:
    "inline-flex items-center gap-2 rounded-[var(--radius-control)] font-medium transition-colors hover:bg-[var(--surface-elevated)] disabled:opacity-50",
  peligro:
    "inline-flex items-center gap-2 rounded-[var(--radius-control)] font-medium transition-opacity hover:opacity-90 disabled:opacity-50",
}

const TAMANO: Record<TamanoBoton, string> = {
  sm: "px-2 py-1 text-xs",
  md: "px-3 py-1.5 text-sm",
}

export function Boton({
  children,
  variante = "secundario",
  tamano = "md",
  className,
  type = "button",
  ...resto
}: {
  children: ReactNode
  variante?: VarianteBoton
  tamano?: TamanoBoton
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={cn(VARIANTE[variante], TAMANO[tamano], className)}
      style={
        variante === "peligro"
          ? { backgroundColor: "var(--destructive)", color: "var(--primary-fg)" }
          : undefined
      }
      {...resto}
    >
      {children}
    </button>
  )
}
